<template>
	<div class="private-order-detail-content" :class="{ 'full-width': fullWidth }">
		<breadcrumb v-if="isHasBreadcrumb" :paths="breadcrumbPaths" :font-size="12" />

		<span v-if="!isProductLoading" class="product-title">
			{{ product.name }}
		</span>
		<div v-else class="product-title-skeleton skeleton"></div>

		<div v-if="!isProductLoading" class="description-wrapper">
			<span class="weight-tex">Berat {{ product.weight }}gr / pcs</span>

			<span class="dot-divider">
				<i class="bi bi-dot"></i>
			</span>

			<span class="origin-location">
				Dikirim dari <b>{{ product.shipping.origin.city_name }}</b>
			</span>
		</div>
		<div v-else class="description-wrapper-skeleton skeleton"></div>

		<badges
			v-if="!isProductLoading"
			:is-lp-enabled="checkProductBadge(product, 'landing_page')"
			:is-marketing-kit-enabled="checkProductBadge(product, 'marketing_kit')"
		/>
		<div v-else class="badges-skeleton skeleton"></div>

		<span v-if="!isProductLoading" class="product-price">{{ rangePrice }}</span>
		<div v-else class="product-price-skeleton skeleton"></div>

		<div v-if="!isProductLoading" class="product-discount">
			<span class="product-discount-price">
				{{ formatMoney(product.distributor.slash_price, 'IDR', false) }}
			</span>
			<discount-tag v-if="discount !== '0'" :discount="discount" />
		</div>
		<div v-else class="product-discount-skeleton skeleton"></div>

		<div v-if="isProductLoading" class="wholesale-title-skeleton skeleton"></div>
		<span v-else-if="isHasWholesale" class="wholesale-title"> Harga Wholesale </span>

		<div v-if="isProductLoading" class="wholesale-skeleton skeleton"></div>
		<private-order-wholesale
			v-else-if="isHasWholesale"
			:wholesales="product.distributor.wholesale"
		/>

		<div v-if="isHasTabMenu" class="description-tab-wrapper" id="product-description-tab">
			<span
				v-if="isHasDescription"
				class="description-tab-item"
				:class="{ active: detailActiveSection === 'product-description' }"
				@click="handleScrollToSectionId('product-description')"
			>
				Deskripsi
			</span>

			<span
				v-if="isHasContent"
				class="description-tab-item"
				:class="{ active: detailActiveSection === 'product-content' }"
				@click="handleScrollToSectionId('product-content')"
			>
				Konten
			</span>
		</div>

		<div v-if="isHasDescription" class="product-description-wrapper" id="product-description">
			<span class="product-description-title">Deskripsi</span>

			<div class="product-description-content" :class="{ expanded: isShowMore }">
				<div ref="descriptionContent" v-html="product.description"></div>
			</div>

			<span v-if="isHasShowMore" class="show-more" @click="isShowMore = !isShowMore">
				{{ showMoreLabel }}
			</span>
		</div>

		<div v-if="isHasDescription" class="divider"></div>

		<div v-if="isHasContent" class="product-description-wrapper" id="product-content">
			<span class="product-description-title">Konten</span>

			<div class="product-content-wrapper">
				<product-content-item
					v-if="isHasMarketingKit"
					button-label="Download"
					icon-class="bi bi-download"
					@click-action="handleClickMarketingKit"
				>
					<template #icon>
						<i class="bi bi-file-earmark-text"></i>
					</template>
					<template #description>
						<span class="conten-item-title">{{ marketingKitLabel }}</span>
					</template>
				</product-content-item>

				<product-content-item
					v-for="(page, index) in product.pages"
					:key="index"
					:button-label="generatePageObject(page).buttonLabel"
					:icon-class="generatePageObject(page).buttonIcon"
					@click-action="handleClickPageUrl(generatePageObject(page).url)"
				>
					<template #icon>
						<i class="bi bi-layout-text-sidebar-reverse"></i>
					</template>
					<template #description>
						<span class="conten-item-title">{{ generatePageObject(page).label }}</span>

						<a
							v-for="(preview, prevIndex) in generatePageObject(page).previews"
							:key="prevIndex"
							:href="preview.url"
							class="elementor-link"
							target="_blank"
						>
							<span>{{ preview.title }}</span>
							<i class="bi bi-box-arrow-up-right"></i>
						</a>
					</template>
				</product-content-item>
			</div>
		</div>
	</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import Breadcrumb from '@/components/Breadcrumb.vue';
import Badges from '@/components/products/Badges.vue';
import DiscountTag from '@/components/private-orders/homepage/DiscountTag.vue';
import PrivateOrderWholesale from '@/components/private-orders/detail/Wholesale.vue';
import ProductContentItem from '@/components/private-orders/detail/ContentItem.vue';
import { formatMoney, getProductBadges } from '@/lib/helper';

export default {
	components: {
		Breadcrumb,
		Badges,
		DiscountTag,
		PrivateOrderWholesale,
		ProductContentItem,
	},
	props: {
		fullWidth: {
			type: Boolean,
			default: false,
		},
		isHasBreadcrumb: {
			type: Boolean,
			default: true,
		},
		showTabMenu: {
			type: Boolean,
			default: true,
		},
		product: {
			type: Object,
			required: true,
		},
		isProductLoading: {
			type: Boolean,
			required: true,
		},
		isHasDescription: {
			type: Boolean,
			required: true,
		},
		isHasContent: {
			type: Boolean,
			required: true,
		},
	},
	data() {
		return {
			descElementHeight: 0,
			isShowMore: false,
		};
	},
	computed: {
		...mapGetters({
			detailActiveSection: 'privateOrder/detailActiveSection',
			detailTargetSectionId: 'privateOrder/detailTargetSectionId',
		}),
		breadcrumbPaths() {
			return [
				{
					title: 'Home',
					url: '/',
				},
				{
					title: 'Wholesale Order',
					url: '/wholesale-order',
				},
			];
		},
		rangePrice() {
			if (this.isProductLoading) return '';

			const wholesale = this.product.distributor.wholesale;
			if (wholesale.length === 0) {
				return this.formatMoney(this.product.distributor.price, 'IDR', false);
			}

			if (wholesale.length === 1) {
				return this.formatMoney(wholesale[0].price, 'IDR', false);
			}

			// eslint-disable-next-line arrow-parens
			const prices = wholesale.map((item) => item.price);
			const maxPrice = Math.max(...prices);
			const minPrice = Math.min(...prices);

			if (maxPrice === minPrice) return this.formatMoney(maxPrice, 'IDR', false);
			return `${this.formatMoney(minPrice, 'IDR', false)} - ${this.formatMoney(
				maxPrice,
				'IDR',
				false,
			)}`;
		},
		discount() {
			if (this.isProductLoading) return '0';

			const wholesale = this.product.distributor.wholesale;
			if (wholesale.length === 0) return '0';

			// eslint-disable-next-line arrow-parens
			const discount = wholesale.map((item) => item.discount);
			return Math.max(...discount).toString();
		},
		isHasTabMenu() {
			return this.showTabMenu && (this.isHasDescription || this.isHasContent);
		},
		isHasMarketingKit() {
			if (this.isProductLoading) return false;

			const marketingKit = this.product?.marketing_kit;
			const hasMarketingKit = marketingKit && marketingKit.visible;
			return hasMarketingKit;
		},
		isHasWholesale() {
			if (this.isProductLoading) return false;
			const wholesale = this.product.distributor?.wholesale;
			if (wholesale && wholesale.length > 0) return true;
			return false;
		},
		isHasShowMore() {
			if (this.descElementHeight > 300) return true;
			return false;
		},
		showMoreLabel() {
			if (this.isShowMore) return 'Lihat Lebih Sedikit';
			return 'Lihat Selengkapnya';
		},
		marketingKitLabel() {
			if (this.isProductLoading) return '';

			const description = this.product.marketing_kit?.description;
			if (description) return description;
			return 'Marketing Kit Product';
		},
	},
	watch: {
		detailTargetSectionId: {
			handler(newValue) {
				if (newValue === '') return;
				const section = document.getElementById(newValue);
				if (section) {
					const sectionRect = section.getBoundingClientRect();
					const absoluteTop = sectionRect.top + window.pageYOffset;

					window.scrollTo({
						top: absoluteTop - 120,
						behavior: 'smooth',
					});
				}
			},
		},
		'product.description': {
			handler(newVal) {
				if (newVal) {
					this.$nextTick(() => {
						const element = this.$refs.descriptionContent;
						if (element) {
							this.descElementHeight = element.offsetHeight;
						}
					});
				}
			},
			immediate: true,
		},
	},
	methods: {
		...mapActions({
			setDetailTargetSectionId: 'privateOrder/setDetailTargetSectionId',
		}),
		getProductBadges,
		formatMoney,
		checkProductBadge(product, key = 'cod') {
			const badges = this.getProductBadges(product);
			return badges[key] ?? false;
		},
		handleScrollToSectionId(sectionId) {
			this.setDetailTargetSectionId(sectionId);
		},
		handleClickMarketingKit() {
			window.open(this.product.marketing_kit.link);
		},
		handleClickPageUrl(url) {
			window.open(url);
		},
		generatePageObject(page) {
			const label = page.type.label;
			const isOO = label === 'Order Online';
			const url = page.url;
			// eslint-disable-next-line arrow-parens
			const previews = page.previews.map((item) => ({
				title: item.title || 'Preview',
				url: item.url,
			}));

			return {
				label,
				url,
				buttonLabel: isOO ? 'Duplicate' : 'Download',
				buttonIcon: isOO ? 'bi bi-copy' : 'bi bi-download',
				previews,
			};
		},
	},
};
</script>

<style scoped lang="scss">
.private-order-detail-content {
	width: 400px;
	min-width: 400px;
	max-width: 400px;
	height: auto;
	display: flex;
	flex-direction: column;
	margin-bottom: 30px;

	@media screen and (min-width: 1280px) {
		width: 508px;
		min-width: 508px;
		max-width: 508px;
	}

	&.full-width {
		width: 100%;
		min-width: auto;
		max-width: 100%;
	}
}

span.product-title {
	font-size: 22px;
	font-weight: 700;
	color: #000000;
	line-height: 28px;
	margin: 4px 0;
	display: -webkit-box;
	-webkit-box-orient: vertical;
	overflow: hidden;
	text-overflow: ellipsis;
	-webkit-line-clamp: 3;
	line-clamp: 3;
}

.product-title-skeleton {
	width: 100%;
	height: 28px;
	margin: 4px 0;
}

.description-wrapper {
	width: 100%;
	height: auto;
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 4px;

	& > span.dot-divider {
		color: rgba(0, 0, 0, 0.4);
	}

	& > span.weight-tex {
		font-size: 12px;
		line-height: 18px;
		line-height: 18px;
	}

	& > span.origin-location {
		font-size: 12px;
		line-height: 18px;
		line-height: 18px;
	}
}

.description-wrapper-skeleton {
	width: 50%;
	height: 18px;
}

.badges-skeleton {
	width: 20%;
	height: 24px;
}

span.product-price {
	color: #ff9f1a;
	font-size: 22px;
	line-height: 28px;
	font-weight: 700;
	margin-top: 12px;
}

.product-price-skeleton {
	width: 40%;
	height: 28px;
	margin-top: 12px;
}

.product-discount {
	display: flex;
	gap: 4px;
	align-items: center;
	margin-top: 4px;

	& > span.product-discount-price {
		text-decoration: line-through;
		font-size: 16px;
	}
}

.product-discount-skeleton {
	width: 30%;
	margin-top: 4px;
	height: 24px;
}

span.wholesale-title {
	font-size: 12px;
	line-height: 18px;
	font-weight: 400;
	color: #414756;
	margin-top: 14px;
	margin-bottom: 8px;
}

.wholesale-title-skeleton {
	width: 15%;
	height: 18px;
	margin-top: 14px;
	margin-bottom: 8px;
}

.wholesale-skeleton {
	width: 100%;
	height: 80px;
}

.description-tab-wrapper {
	width: 100%;
	height: auto;
	padding: 12px;
	border-top: 1px solid #d9dadd;
	border-bottom: 1px solid #d9dadd;
	margin-top: 24px;
	display: flex;
	flex-direction: row;
	gap: 12px;

	& > span.description-tab-item {
		font-size: 16px;
		font-weight: 600;
		line-height: 24px;
		color: #80848e;
		cursor: pointer;
		transition: 0.1s all ease;

		&.active {
			color: #006dc7;
		}
	}
}

.product-description-wrapper {
	width: 100%;
	height: auto;
	display: flex;
	flex-direction: column;
	gap: 10px;
	margin-top: 16px;

	& > span.product-description-title {
		font-size: 16px;
		font-weight: 600;
		line-height: 18px;
		color: #000000;
	}
}

.product-content-wrapper {
	width: 100%;
	height: auto;
	display: flex;
	flex-direction: column;
	gap: 8px;
}

span.conten-item-title {
	size: 16px;
	font-weight: 400;
	line-height: 24px;
	color: #2b2f39;
}

a.elementor-link {
	text-decoration: none;
	cursor: pointer;
	color: #006dc7;
	display: flex;
	gap: 4px;
	font-size: 14px;
}

.divider {
	width: 100%;
	height: auto;
	border: 0.5px solid #d9dadd;
	margin-top: 16px;
}

.product-description-content {
	width: 100%;
	height: auto;
	max-height: 310px;
	overflow-y: hidden;

	&.expanded {
		max-height: none;
	}
}

span.show-more {
	font-size: 14px;
	line-height: 20px;
	font-weight: 600;
	color: #006dc7;
	cursor: pointer;
}
</style>
